import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Box,
  Button,
  Controller,
  ControllerMenuItem,
  ControllerModal,
  ControllerValidatedSelect,
  ControllerValidatedTextField,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useForm,
  yupResolver,
} from '@bymason-private/legacy-pattern-components';
import {
  NotificationModel,
  useCreateNotification,
  useDeleteNotification,
} from '@bymason/controller-api-hooks';
import { mapNotificationToFormModel, mapFormModelToNotification } from './logics';
import { initialValues, Fields, schema } from './types';

interface CreateEditNotificationModalProps {
  open: boolean;
  notification?: NotificationModel;
  onClose: () => void;
  onSuccess?: (notification: NotificationModel, withError?: 'delete-error') => void;
  onError?: (message: string, code?: number) => void;
}

const DATA_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

export function CreateEditNotificationModal({
  onClose,
  onSuccess,
  onError,
  open,
  notification,
}: CreateEditNotificationModalProps) {
  const { authToken = '', userInfo } = useAuth();
  const {
    control,
    formState: { isValid },
    getValues,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: notification
      ? mapNotificationToFormModel(notification)
      : {
          ...initialValues,
          [Fields.Recipients]: userInfo?.email ?? '',
        },
    resolver: yupResolver(schema),
  });
  const [
    createNotification,
    createNotificationResult,
    createNotificationError,
    createNotificationBusy,
  ] = useCreateNotification(authToken);
  const [deleteNotification, deleteError, deleteBusy] = useDeleteNotification(authToken);
  const [deleteStarted, setDeleteStarted] = useState(false);

  const isUpdate = useMemo(() => {
    return !!notification;
  }, [notification]);

  const handleSubmit = useCallback(() => {
    createNotification(mapFormModelToNotification(getValues()));
  }, [createNotification, getValues]);

  useEffect(() => {
    if (isUpdate && createNotificationResult && !deleteStarted) {
      setDeleteStarted(true);
      deleteNotification(notification?.id ?? '');
    }
  }, [isUpdate, notification?.id, deleteNotification, createNotificationResult, deleteStarted]);

  useEffect(() => {
    if (createNotificationResult) {
      if (isUpdate && deleteStarted && !deleteBusy) {
        onSuccess && onSuccess(createNotificationResult, deleteError ? 'delete-error' : undefined);
        onClose();
      } else if (!isUpdate) {
        onSuccess && onSuccess(createNotificationResult);
        onClose();
      }
    }
  }, [createNotificationResult, deleteBusy, deleteError, onClose, onSuccess, isUpdate, deleteStarted]);

  useEffect(() => {
    if (onError && createNotificationError && !createNotificationBusy) {
      onError(createNotificationError);
    }
  }, [createNotificationError, createNotificationBusy, onError]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <ControllerModal
      onClose={onClose}
      open={open}
      title={`${notification ? 'Edit' : 'Create'} notification rule`}
      data-modal-test='create-notification'
    >
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <Typography>We will email your recipients when the usage threshold has been crossed.</Typography>
          <ControllerValidatedTextField
            label='Rule Name'
            name={Fields.Name}
            control={control}
            fullWidth
            required
            helperText='60 character limit'
          />
          <ControllerValidatedTextField
            name={Fields.Description}
            control={control}
            label='Rule Description'
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            helperText='1,000 character limit'
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ControllerValidatedTextField
              name={Fields.Threshold}
              control={control}
              label='Usage threshold'
              required
              fullWidth
              helperText='Usage threshold will be rounded to the nearest byte'
            />
            <ControllerValidatedSelect
              name={Fields.Unit}
              required
              control={control}
              sx={{ width: 100 }}
              onClick={() => {
                trigger();
              }}
            >
              {DATA_UNITS.map((du) => (
                <ControllerMenuItem key={du} value={du}>
                  {du}
                </ControllerMenuItem>
              ))}
            </ControllerValidatedSelect>
          </Box>
          <FormControl sx={{ mt: 1 }}>
            <FormLabel required>Base threshold on</FormLabel>
            <Controller
              rules={{ required: true }}
              control={control}
              name={Fields.Domain}
              render={({ field }) => {
                return (
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value='account'
                      control={<Radio sx={{ color: 'gray', ml: 0.5 }} />}
                      label='Total account usage'
                    />
                    <FormControlLabel
                      value='account-sim'
                      control={<Radio sx={{ color: 'gray', ml: 0.5 }} />}
                      label='Individual sim usage'
                    />
                  </RadioGroup>
                );
              }}
            />
          </FormControl>
          <ControllerValidatedTextField
            name={Fields.Recipients}
            control={control}
            label='Recipients'
            multiline
            minRows={2}
            maxRows={4}
            required
            fullWidth
            helperText='Separate each email with a comma'
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSubmit} size='medium' variant='contained' disabled={!isValid}>
          {notification ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
